import { escapeRegExp } from 'lodash';
import { DynamicPath, Mapping } from '../types';

/**
 * Determine if a mapping path is a dynamic path by checking for the occurence
 * of dynamic path syntax.
 * @param path Path to evaluate
 */
export function isDynamicPath(path: string) {
  const DYNAMIC_PATH_REGEX = /\w+\[\w+@segment:\w+]/g;
  return DYNAMIC_PATH_REGEX.test(path);
}

/**
 * From a path, determine segment and variable names for each dynamic pathing.
 * @param dynamicPath Dynamic path
 * @returns All dynamic path info for a single path
 */
function getDynamicPathInfo(path: string): DynamicPath[] {
  const DYNAMIC_PATH_REGEX = /\[(?<variable_name>\w+)@segment:(?<segment_name>\w+)]/g;
  const dynamicPathComponents = [...path.matchAll(DYNAMIC_PATH_REGEX)];
  const allDynamicPathComponents = dynamicPathComponents.map(components => {
    return components.groups as unknown as DynamicPath;
  });
  return allDynamicPathComponents;
}

/**
 * Check if the given column is mapped by a dynamic path.
 * @param colPath Column path from sample data
 * @param dynamicPathMappings All mappings that use dynamic paths
 * @returns Whether the column is mapped by a dynamic path.
 */
function checkColumnUsedByDynamicPath(colPath: string, dynamicPathMappings: Mapping[]): boolean {
  for (const mapping of dynamicPathMappings) {
    const dynamicPathInfo = getDynamicPathInfo(mapping.path);
    // searches for BLAH[INDEX].ATTR - if it's there, then there could be a mapping
    // of it via a dynamic path
    const COLPATH_SEGMENT_REGEX = /\w+\[\d+]\.\w+/g;
    const candidateDynPath = colPath.match(COLPATH_SEGMENT_REGEX);

    // on match, look through dynamic paths
    if (candidateDynPath) {
      // [0] => only working with single dynamic paths, not nested.
      const colPathSegment = candidateDynPath[0].split('[')[0];
      // get the attribute, e.g. the column path 'diagnosis_detail[0].diagnosis_code'
      // has the attribute 'diagnosis_code'
      const attribute = candidateDynPath[0].split('].')[1];

      // we assume a column is mapped via a dynamic path if the following conditions are held:
      // 1. the column appears in the dynamic path,
      // 2. the mapping value uses both the dynamic path's variable name and attribute
      // for example, the value 'aaa.diagnosis_code' should refer to a dynamic path if there
      // is a dynamic path with the variable 'aaa' and the attribute in the column is 'diagnosis_code'.
      for (const dynamicPath of dynamicPathInfo) {
        if (
          dynamicPath &&
          dynamicPath.segment_name === colPathSegment &&
          mapping.value.includes(`${dynamicPath.variable_name}.${attribute}`)
        ) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Used by getUnmappedColumns while walking through sample data.
 * Determines whether data is unmapped and needs to be flagged as such.
 * @param path Path to field in sample data
 * @param mappings Complete set of mappings for a file
 * @param paths Array of paths in sample data that are unmapped
 * @returns Updates paths Array with missing data paths
 */
function pushToPaths(path: string, mappings: Mapping[], paths: string[]) {
  const mappingValues = mappings.map(({ value }) => value);
  const mappingsWithDynamicPaths = mappings.filter(mapping => {
    return isDynamicPath(mapping.path);
  });
  /*
   * regTest is a dynamic regTest pattern used to see if the sample data path is found/used in any mapping value
   * the regTest changes based on which path we are searching for in the mappings
   * Ex: When finalPath = _.path, the regTest = _\.path(?!([\w\.]))|_\.path\.[\w]*\(.*\)
   * regTest will match all references of the finalPath as long as it's not followed by more words or digits
   * It will also match the finalPath when a method is used on it
   * _.path could be matched in any of the following... #1 groovyFunc(_.path) #2 _.path.toString() #3 'name' + _.path
   */
  const regTest = new RegExp(
    `${escapeRegExp(path)}(?!([\\w\\.]))|${escapeRegExp(path)}\\.[\\w]*\\(.*\\)`,
  );
  const pathInUse = mappingValues.find(mv => {
    return mv !== null && !!mv.match(regTest);
  });
  const usedByDynamicPath = checkColumnUsedByDynamicPath(path, mappingsWithDynamicPaths);

  if (!pathInUse && !usedByDynamicPath) {
    paths.push(path);
  }
}

/**
 * From sample data that is to be converted to FHIR, get all columns
 * in the data that are not used in the mappings (i.e. not mapped).
 * @param sampleData All input sample data
 * @param mappings Complete set of mappings for a file
 * @returns Unmapped columns of the sample data
 */
export function getUnmappedColumns(sampleData: Record<string, any>, mappings: Mapping[]): string[] {
  let paths: string[] = [];

  const mappingValues = mappings.map(({ value }) => value);

  /**
   * Recurse to build paths from sample data object ex: obj1.obj2.obj3
   * @param obj Remaining object to traverse
   * @param currentPath How path has been constructed so far in the traversal
   */
  function walk(obj: any, pathsList: string[], currentPath = '_') {
    const path = currentPath || '';

    for (const [key, value] of Object.entries(obj)) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        // Handles edge case where valu is an arrayof values, not objects
        // Checks to see if all values in the array are used in mappings
        if (
          value instanceof Array &&
          value.length > 0 &&
          value.some(element => typeof element !== 'object')
        ) {
          value.forEach((element, i) => {
            let valuePath = `${key}[${i}]`;

            if (typeof element === 'object') {
              walk(obj[key], pathsList, `${path}.${key}`);
            } else {
              // handle arrays in dynamic path
              if (path !== '_') {
                valuePath = `${path}.${key}[${i}]`;
              }

              pushToPaths(valuePath, mappings, pathsList);
            }
          });
        } else if (value !== null && (typeof value === 'object' || value instanceof Array)) {
          const isArrayIndex = /^\d+$/.test(key);

          if (isArrayIndex) {
            walk(obj[key], pathsList, `${path}[${key}]`);
          } else {
            walk(obj[key], pathsList, `${path}.${key}`);
          }
        } else if (!Array.isArray(obj)) {
          const finalPath = `${path}.${key}`;
          pushToPaths(finalPath, mappings, pathsList);
        }
      }
    }
  }

  walk(sampleData, paths);
  // handle __ in mapping values
  // all keys below an object referenced by __. are considered mapped
  const mappingsWithDunder: string[] = mappingValues.filter(x => {
    return x !== null && x.includes('__.');
  });

  if (mappingsWithDunder.length > 0) {
    mappingsWithDunder.forEach(m => {
      const dunderMatches = m.match(/__\.\w+/g);

      if (dunderMatches) {
        dunderMatches.forEach(dm => {
          paths = paths.filter(p => !p.includes(dm.replace('__', '')));
        });
      }
    });
  }

  // make output look prettier
  return paths.map(p => p.replace('_.', ''));
}
